import { FunctionComponent, memo } from "react";
import { IProps, ITagFormValues } from "./tag-form.types";
import useTags from "hooks-for/tags/use-tags";
import { Button, FieldError, SpinnerUntil } from "@edgetier/components";
import { Field, Form, Formik } from "formik";
import { faSave } from "@fortawesome/pro-solid-svg-icons";
import SelectTagGroups from "components-for/filters/select-tag-groups";
import { getValidationSchema } from "./tag-form.utilities";
import "./tag-form.scss";
import useTagGroups from "hooks-for/tag-groups/use-tag-groups";

/**
 * A form for making changes to a tag.
 * @param props.initialValues A tag to initialise the form with.
 * @param props.onSubmit      A function that runs when the form is submitted.
 * @param props.tagGroupType  Maybe restrict the available groups to a particular type.
 * @returns
 */
const TagForm: FunctionComponent<IProps> = ({ initialValues, onSubmit, tagGroupType }) => {
    const { data: tagNames, isLoading } = useTags(
        {},
        { select: (tags) => tags.map(({ tag }) => tag).filter((tag) => tag !== initialValues.tag) }
    );
    const { data: tagGroups } = useTagGroups({ tagGroupType });

    /**
     * Trim the values before calling the onSubmit function.
     * @param values The tag values.
     */
    const preSubmit = (validationSchema: ReturnType<typeof getValidationSchema>) => (values: ITagFormValues) => {
        const validatedValues = validationSchema.validateSync(values);
        const tagGroup = tagGroups?.find(({ tagGroupId }) => tagGroupId === values.tagGroupId)?.tagGroup ?? "";
        const trimmed = { ...validatedValues, tag: values.tag.trim(), tagGroup };
        onSubmit(trimmed);
    };

    return (
        <SpinnerUntil<string[]> data={tagNames} isReady={!isLoading}>
            {(tagNames) => {
                const validationSchema = getValidationSchema(tagNames);
                return (
                    <Formik
                        initialValues={initialValues}
                        onSubmit={preSubmit(validationSchema)}
                        validationSchema={validationSchema}
                    >
                        {({ isSubmitting }) => (
                            <Form name="tagGroupForm">
                                <div className="field">
                                    <label htmlFor="tag">Tag</label>
                                    <Field
                                        type="text"
                                        name="tag"
                                        id="tag"
                                        placeholder="New tag name&hellip;"
                                        autoFocus={true}
                                    />
                                    <FieldError name="tag" />
                                </div>

                                <div className="field">
                                    <label htmlFor="tagGroupId">Tag Group</label>
                                    <SelectTagGroups isSingleSelect name="tagGroupId" tagGroupType={tagGroupType} />
                                    <FieldError name="tagGroupId" />
                                </div>
                                <Button isLoading={isSubmitting} icon={faSave} styleName="positive" type="submit">
                                    Save
                                </Button>
                            </Form>
                        )}
                    </Formik>
                );
            }}
        </SpinnerUntil>
    );
};

export default memo(TagForm);
